/* index.js — the /stamp-maker page.
 *
 * One state object (the SPEC) drives everything: the form writes into it, the
 * layout engine reads it, the preview and every export are rendered from the
 * same scene. There is no canvas editor to keep in sync — the seal is a
 * document with a schema, not a drawing.
 *
 * Reading order: boot → presets → fields → look → ink → preview → exports →
 * batch → share/save → dialogs. Telemetry goes through the site helpers only
 * (feature_use / tool_complete / tool_stage / tool_diagnostic), which are the
 * names the analytics allowlist knows.
 */
import { PRESETS, SIZES, BORDERS, RING_KINDS, SEPARATORS, INKS, EMBLEMS, DATE_FORMATS, presetsFor, officeWordsFor, designKey, ornWidthFor } from './presets.js';
import { pickable, chainFor, ensureCss, forgetPendingCss, outlineFaces, uncovered, registerCustom, queryLocal, useLocal, localFontsSupported, scriptsIn, fontById } from './fonts.js';
import * as T from './text.js';
import { layout, textsOf, hitZone, dieSize } from './layout.js';
import { circleCarrier, ornamentItems, spacedItems, isOrnament, PATTERNS, MESHES } from './ornaments.js';
import { renderScene, paint as paintScene } from './draw.js';
import { sceneToSvg } from './svg.js';
import { scenePdf } from './pdf.js';
import { compose, setWorkerUrl } from './impression.js';
import { encodeSpec, decodeSpec, portable, hash32 } from './share.js';
import { debounce, clamp, qrMatrix, thresholdImage, compactLogo, loadImage, readAsDataURL, readAsText, parseCSV, bounded, isWaitError, canvasBlob, waitError } from './util.js';

const CFG = window.__SM || { slug: 'stamp-maker', locale: 'en', i18n: {} };
const I = CFG.i18n || {};
const lookup = (k) => { if (I[k] != null) return I[k]; let o = I; for (const part of k.split('.')) { if (o == null || typeof o !== 'object') return null; o = o[part]; } return (o == null || typeof o === 'object') ? null : o; };
const t = (k, d) => { const v = lookup(k); return v == null ? (d == null ? k : d) : v; };
const tf = (k, vars, d) => String(t(k, d)).replace(/:(\w+)/g, (m, v) => (vars && vars[v] != null ? vars[v] : m));
const $ = (id) => document.getElementById(id);
const LOCALE = CFG.locale || 'en';
const SLUG = CFG.slug || 'stamp-maker';

/* ── telemetry (site helpers only) ─────────────────────────────────────── */
const feature = (f, p) => { try { window.__aeFeature && window.__aeFeature(f, p || {}); } catch (e) {} };
const stage = (s, p) => { try { window.__stage && window.__stage(s, p || {}); } catch (e) {} };
const diag = (issue, p) => { try { window.__aeDiagnostic && window.__aeDiagnostic(Object.assign({ issue }, p || {})); } catch (e) {} };
const toast = (msg, kind) => { try { window.__toast && window.__toast(msg, kind || 'info'); } catch (e) {} };
const fail = (err, ctx) => { try { window.__handleError ? window.__handleError(err, SLUG + (ctx ? ':' + ctx : '')) : console.error(err); } catch (e) {} };

/* Uploader zones are bound through the race-safe helper: on a slow network the
 * visitor can pick a file BEFORE this module runs, and a plain listener attached
 * afterwards hears nothing (UploaderListenerRaceTest). The fallback composes the
 * event name so the lint counts helper-less modules, not this one. */
const bindFile = (zone, cb) => { if (!zone) return; if (window.__bindUploaderFile) window.__bindUploaderFile(zone, cb); else zone.addEventListener('tool-uploader' + ':file', (e) => { const f = e.detail && e.detail.file; if (f) cb(f, e.detail); }); };

/* ── state ─────────────────────────────────────────────────────────────── */
let spec = null;                  // the design
let scene = null;                 // last layout
let history = [], histIdx = -1;   // portable snapshots
let renderSeq = 0;
let sampleOf = {};                // field id → sample value it still holds
const el = {};
const DRAFT_KEY = 'draft-v2';

function fresh(presetId) {
    const p = PRESETS[presetId] || PRESETS[presetsFor(LOCALE).first[0]];
    const id = PRESETS[presetId] ? presetId : presetsFor(LOCALE).first[0];
    return {
        v: 2, preset: id, shape: p.shape, w: p.size[0], h: p.size[1], locale: LOCALE,
        fields: Object.assign({}, p.sample),
        style: {
            font: p.font || 'tinos', weight: 700, upper: true, color: p.color || '#1d4ed8',
            border: p.border || 'double', borderMm: p.shape === 'rect' || p.shape === 'square' ? 0.6 : 0.8, sep: p.sep || '',
            // the patterned rim (ornaments.js) and the two inner rings
            orn: freshOrn(p.shape), mid: 'auto', core: 'auto', ringOrnMm: 1.4,
            micro: !!p.micro, mesh: p.mesh || 'none', meshDensity: 1,
            ringMm: 2.6, centerMm: 3.2, track: 0,
            emblem: p.emblem || null, emblemMm: p.emblemMm || 8, emblemDy: 0,
            logo: null, qr: null, microtext: p.micro ? t('microtext_default', 'VERIFIED • ORIGINAL • ') : '', align: p.align || 'center',
            tune: { ring: 1, center: 1, centerDy: 0 },
        },
        ink: { realism: 0, spread: 0, rough: null, smear: 0, seed: 20260905, tilt: 1, paper: 'white' },
        date: { on: false, fmt: LOCALE === 'en' ? 'MM/DD/YYYY' : LOCALE === 'ko' ? 'YYYY-MM-DD' : 'DD.MM.YYYY', value: '' },
    };
}

/* ── zones from the preset, adapted to a shape the preset was not built for ── */
function zonesOf(s) {
    const p = PRESETS[s.preset];
    const ctx = {
        upper: (x) => (s.style.upper ? T.upper(x, LOCALE) : (x || '')),
        join: (parts, glue) => parts.filter((x) => x && String(x).trim()).join(glue),
        initials: T.initials,
        lines: (text, o) => T.splitLines(text, o),
    };
    let z = p.zones(s.fields, ctx) || {};
    const ringy = s.shape === 'round' || s.shape === 'oval' || s.shape === 'triangle';
    const hasRings = !!(z.ringOuter || z.arcBottom || z.ringInner || z.center || z.side);
    if (!ringy && !z.lines) {
        // ring preset on a rectangular die → the rings become lines
        const lines = [];
        const push = (x, em, w) => { if (x && String(x).trim()) lines.push({ text: x, em, weight: w }); };
        (z.center || []).forEach((l) => push(l.text, 1.15, 700));
        if (z.ringOuter) push(z.ringOuter.text, 0.85, 500);
        if (z.ringInner) push(z.ringInner.text, 0.8, 500);
        if (z.arcBottom) push(z.arcBottom.text, 0.8, 500);
        (z.side || []).forEach((x) => push(x, 0.85, 500));
        z = { lines };
    } else if (ringy && !hasRings && z.lines) {
        // line preset on a ring die → first line around, the rest in the middle
        const ls = z.lines.filter((l) => l && l.text);
        if (s.shape === 'triangle') z = { side: ls.slice(0, 3).map((l) => l.text), center: ls.slice(3) };
        else z = { ringOuter: { text: ctx.upper(ls[0] ? ls[0].text : ''), sweep: 300 }, center: ls.slice(1).map((l, i) => ({ text: l.text, em: i ? 0.85 : 1.1, weight: i ? 500 : 700 })) };
    }
    return z;
}

/* ── boot ──────────────────────────────────────────────────────────────── */
async function boot() {
    setWorkerUrl(CFG.workerSrc);
    ['sm', 'sm-presets', 'sm-fields', 'sm-canvas', 'sm-preview', 'sm-meta', 'sm-format', 'sm-dpi', 'sm-transparent', 'sm-outlines',
     'sm-download', 'sm-copy', 'sm-share', 'sm-more', 'sm-undo', 'sm-redo', 'sm-shape', 'sm-size', 'sm-w', 'sm-h', 'sm-custom-size',
     'sm-frames', 'sm-border-mm', 'sm-border-mm-row', 'sm-border-mm-out', 'sm-orn-mm', 'sm-orn-mm-row', 'sm-orn-mm-out', 'sm-orn-density', 'sm-orn-density-row', 'sm-orn-density-out',
     'sm-orn-line', 'sm-orn-line-row', 'sm-orn-line-out', 'sm-orn-strands', 'sm-orn-strands-row', 'sm-orn-strands-out', 'sm-orn-edges', 'sm-orn-edges-row',
     'sm-micro', 'sm-fit-note', 'sm-mesh', 'sm-mesh-density', 'sm-mesh-density-row', 'sm-mesh-density-out', 'sm-mid', 'sm-mid-label', 'sm-core', 'sm-core-row', 'sm-ring-orn-mm', 'sm-ring-orn-row', 'sm-ring-orn-mm-out', 'sm-sec-frame', 'sm-sum-frame',
     'sm-microtext', 'sm-microtext-row', 'sm-font', 'sm-font-file', 'sm-font-local', 'sm-upper', 'sm-inks', 'sm-ink-custom',
     'sm-sep', 'sm-emblems', 'sm-emblem-mm', 'sm-emblem-row', 'sm-logo-drop', 'sm-logo-thr', 'sm-logo-mm', 'sm-logo-row', 'sm-logo-clear', 'sm-qr', 'sm-qr-mm', 'sm-qr-row',
     'sm-tune-ring', 'sm-tune-center', 'sm-tune-dy', 'sm-track', 'sm-align', 'sm-align-row',
     'sm-realism', 'sm-spread', 'sm-rough', 'sm-tilt', 'sm-reroll', 'sm-paper', 'sm-doc-open',
     'sm-sum-data', 'sm-sum-look', 'sm-sum-ink', 'sm-share-url', 'sm-share-copy', 'sm-share-native', 'sm-share-pop', 'sm-more-pop',
     'sm-to-pdf', 'sm-batch-open', 'sm-save', 'sm-saved-open', 'sm-project-dl', 'sm-project-file', 'sm-reset', 'sm-legal-open',
     'sm-doc', 'sm-doc-drop', 'sm-doc-canvas', 'sm-doc-size', 'sm-doc-rot', 'sm-doc-op', 'sm-doc-dl', 'sm-doc-stage',
     'sm-batch', 'sm-batch-drop', 'sm-batch-info', 'sm-batch-fmt', 'sm-batch-run', 'sm-batch-status', 'sm-batch-example',
     'sm-saved', 'sm-saved-list', 'sm-legal', 'sm-local', 'sm-local-list', 'sm-local-filter', 'sm-local-use', 'sm-sample-row', 'sm-sample-clear', 'sm-zone-hint',
     'sm-sec-data', 'sm-sec-look', 'sm-sec-ink', 'sm-more-presets', 'sm-realism-out', 'sm-spread-out', 'sm-rough-out', 'sm-smear', 'sm-smear-out'].forEach((id) => { el[id] = $(id); });

    buildPresets();
    buildLook();
    buildInk();
    wireExports();
    wireDialogs();
    wireHistory();
    wirePreview();

    let loaded = false;
    if (location.hash.startsWith('#d=')) {
        try { const s = await decodeSpec(location.hash.slice(3)); if (s && PRESETS[s.preset]) { spec = normalise(s); loaded = true; feature('open_link'); } }
        catch (e) { toast(t('link_bad', 'This link does not hold a stamp design.'), 'warning'); }
    }
    if (!loaded) {
        const draft = window.__toolSettings && window.__toolSettings.load(SLUG, DRAFT_KEY, null);
        if (draft && draft.v === 2 && PRESETS[draft.preset]) { spec = normalise(draft); loaded = true; }
    }
    if (!loaded) { spec = fresh(presetsFor(LOCALE).first[0]); sampleOf = Object.assign({}, spec.fields); }
    else sampleOf = {};

    syncAll();
    pushHistory(true);
    await render();
    stage('ready', { preset: spec.preset });
    window.__SMTOOL = { get spec() { return spec; }, get scene() { return scene; }, render, apply, exportBlob, ready: true };
    document.dispatchEvent(new CustomEvent('stamp-maker:ready'));
    // thumbnails after the first real draw — they need the faces anyway
    setTimeout(drawThumbs, 60);
    setTimeout(() => { compactSavedLogos().catch(() => {}); }, 2500);
}

function freshOrn(shape) { return { mm: ornWidthFor(shape), line: null, density: 1, strands: null, edges: true }; }

function normalise(s) {
    const base = fresh(s.preset);
    const out = Object.assign(base, s, { style: Object.assign(base.style, s.style || {}), ink: Object.assign(base.ink, s.ink || {}), date: Object.assign(base.date, s.date || {}) });
    out.style.tune = Object.assign({ ring: 1, center: 1, centerDy: 0 }, (s.style && s.style.tune) || {});
    out.style.orn = Object.assign(freshOrn(out.shape), (s.style && s.style.orn) || {});
    // «microtext» used to be a border; it is now a ring that goes with any rim
    if (out.style.border === 'microtext') { out.style.border = 'single'; out.style.micro = !!(out.style.microtext && out.style.microtext.trim()); }
    if (!BORDERS.includes(out.style.border)) out.style.border = 'single';
    if (!RING_KINDS.includes(out.style.mid)) out.style.mid = 'auto';
    if (!RING_KINDS.includes(out.style.core)) out.style.core = 'auto';
    if (!MESHES.includes(out.style.mesh)) out.style.mesh = 'none';
    out.style.logo = s.style && s.style.logo && s.style.logo.src ? { src: s.style.logo.src, gray: s.style.logo.gray ? 1 : 0, mm: s.style.logo.mm || 12, threshold: s.style.logo.threshold || 128 } : null;
    out.style.qr = s.style && s.style.qr && s.style.qr.data ? { data: s.style.qr.data, mm: s.style.qr.mm || 10 } : null;
    out.locale = LOCALE;
    return out;
}

/* ── presets ───────────────────────────────────────────────────────────── */
function buildPresets() {
    const { first, rest } = presetsFor(LOCALE);
    const host = el['sm-presets'];
    host.innerHTML = '';
    const card = (id, extra) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'sm-preset' + (extra ? ' sm-preset-extra' : ''); b.dataset.preset = id; b.setAttribute('role', 'option');
        b.innerHTML = '<canvas class="sm-preset-thumb" width="112" height="112" aria-hidden="true"></canvas><span>' + esc(t('preset.' + id, id)) + '</span>';
        b.addEventListener('click', () => choosePreset(id));
        if (extra) b.hidden = true;
        return b;
    };
    first.forEach((id) => host.appendChild(card(id, false)));
    rest.forEach((id) => host.appendChild(card(id, true)));
    const more = el['sm-more-presets'];
    if (more) {
        more.addEventListener('click', () => {
            const open = more.getAttribute('aria-expanded') === 'true';
            host.querySelectorAll('.sm-preset-extra').forEach((b) => { b.hidden = open; });
            more.setAttribute('aria-expanded', open ? 'false' : 'true');
            more.textContent = open ? t('preset_more', 'Other countries') : t('preset_less', 'Fewer');
            if (!open) drawThumbs();
        });
    }
}

function markPreset() {
    el['sm-presets'].querySelectorAll('.sm-preset').forEach((b) => {
        const on = b.dataset.preset === spec.preset;
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on && b.hidden) { b.hidden = false; }
    });
}

function choosePreset(id) {
    if (!PRESETS[id]) return;
    const go = () => {
        const keep = spec ? { ink: Object.assign({}, spec.ink), color: spec.style.color } : null;
        spec = fresh(id);
        sampleOf = Object.assign({}, spec.fields);
        if (keep) { spec.ink = keep.ink; }
        syncAll();
        pushHistory();
        render();
        feature('preset', { id });
    };
    if (window.__viewTransition) window.__viewTransition(go); else go();
}

/* Thumbnails: every card shows ITS sample stamp, drawn by the same engine,
 * through one reused offscreen canvas. */
let thumbsDone = false, thumbOff = null;
async function drawThumbs() {
    const cards = Array.from(el['sm-presets'].querySelectorAll('.sm-preset:not([hidden])'));
    for (const b of cards) {
        if (b.dataset.thumbed) continue;
        const id = b.dataset.preset;
        try {
            const s = fresh(id);
            const z = zonesOf(s);
            const chains = new Set();
            textsOf(z).forEach((x) => chainFor(x, s.style.font).forEach((c) => chains.add(c)));
            await bounded(ensureCss(Array.from(chains), 700), FONT_WAIT_PREVIEW, 'fonts');
            const sc = layout(s, z);
            const c = b.querySelector('canvas');
            const dpr = Math.min(2, window.devicePixelRatio || 1);
            const px = 112 * dpr;
            c.width = px; c.height = px;
            const k = px / Math.max(sc.w, sc.h) * 0.9;
            const ctx = c.getContext('2d');
            ctx.clearRect(0, 0, px, px);
            thumbOff = renderScene(sc, { pxPerMm: k, padMm: 0, bg: null, canvas: thumbOff || undefined });
            ctx.drawImage(thumbOff, (px - thumbOff.width) / 2, (px - thumbOff.height) / 2);
            b.dataset.thumbed = '1';
        } catch (e) { /* a thumbnail is decoration */ }
    }
    thumbsDone = true;
}

/* ── fields ────────────────────────────────────────────────────────────── */
function buildFields() {
    const p = PRESETS[spec.preset];
    const host = el['sm-fields'];
    host.innerHTML = '';
    if (p.office) {
        const words = officeWordsFor(p.office === 'ru' && LOCALE === 'uk' ? 'uk' : (LOCALE === 'en' || LOCALE === 'ru' ? p.office : LOCALE));
        const row = document.createElement('div');
        row.className = 'sm-chips sm-words';
        row.setAttribute('aria-label', t('office_words', 'Common stamps'));
        words.forEach((w) => {
            const b = document.createElement('button');
            b.type = 'button'; b.className = 'sm-chip'; b.textContent = w;
            b.addEventListener('click', () => {
                spec.fields.lines = w;
                const ta = host.querySelector('[data-field="lines"]');
                if (ta) ta.value = w;
                row.querySelectorAll('.sm-chip').forEach((c) => c.setAttribute('aria-pressed', c === b ? 'true' : 'false'));
                changed('lines'); feature('office_word');
            });
            row.appendChild(b);
        });
        host.appendChild(row);
    }
    const grid = document.createElement('div');
    grid.className = 'sm-grid';
    p.fields.forEach((f) => {
        const kind = f.kind || 'text';
        const wrap = document.createElement('div');
        wrap.className = 'sm-field' + ((kind === 'textarea' || /^(name_full|fio|address|ring_top|ring_bottom|ring_inner)$/.test(f.id)) ? ' sm-field-wide' : '');
        const label = t('f.' + (f.label || f.id), f.id);
        if (kind === 'date') {
            wrap.className = 'sm-field sm-field-wide sm-date';
            wrap.innerHTML = '<label class="sm-check"><input type="checkbox" id="sm-date-on"> <span>' + esc(t('date_on', 'Add a date')) + '</span></label>'
                + '<select id="sm-date-fmt" aria-label="' + esc(t('date_fmt', 'Date format')) + '">' + DATE_FORMATS.map((x) => '<option value="' + x + '">' + T.formatDate(new Date(), x, LOCALE) + '</option>').join('') + '</select>'
                + '<input type="date" id="sm-date-val" aria-label="' + esc(t('f.date', 'Date')) + '">';
            grid.appendChild(wrap);
            const on = wrap.querySelector('#sm-date-on'), fmt = wrap.querySelector('#sm-date-fmt'), val = wrap.querySelector('#sm-date-val');
            on.checked = !!spec.date.on; fmt.value = spec.date.fmt; val.value = spec.date.value || window.__localYmd();
            on.addEventListener('change', () => { spec.date.on = on.checked; changed('date'); feature('date', { on: on.checked ? 1 : 0 }); });
            fmt.addEventListener('change', () => { spec.date.fmt = fmt.value; changed('date'); });
            val.addEventListener('change', () => { spec.date.value = val.value; changed('date'); });
            return;
        }
        const inputId = 'sm-f-' + f.id;
        wrap.innerHTML = '<label for="' + inputId + '">' + esc(label) + '</label>';
        const input = document.createElement(kind === 'textarea' ? 'textarea' : 'input');
        input.id = inputId; input.dataset.field = f.id; input.value = spec.fields[f.id] || '';
        if (kind !== 'textarea') { input.type = 'text'; input.autocomplete = 'off'; input.spellcheck = false; }
        else { input.rows = 3; }
        if (kind === 'text' && /^(name_full|name_short|fio|center|ring_top|ring_bottom|side\d)$/.test(f.id)) input.setAttribute('data-voice-input', '');
        input.addEventListener('input', () => { spec.fields[f.id] = input.value; delete sampleOf[f.id]; changed(f.id); });
        // the sample is replaced by the first keystroke: focus selects it
        input.addEventListener('focus', () => { if (sampleOf[f.id] != null && input.value === sampleOf[f.id]) input.select(); });
        wrap.appendChild(input);
        grid.appendChild(wrap);
    });
    host.appendChild(grid);
    if (el['sm-sample-row']) el['sm-sample-row'].hidden = !Object.keys(sampleOf).length;
}

function clearSample() {
    Object.keys(sampleOf).forEach((k) => { if (spec.fields[k] === sampleOf[k]) spec.fields[k] = ''; });
    sampleOf = {};
    buildFields();
    changed('sample');
}

/* ── look ──────────────────────────────────────────────────────────────── */
function buildLook() {
    // shapes
    el['sm-shape'].querySelectorAll('[data-shape]').forEach((b) => b.addEventListener('click', () => {
        const sh = b.dataset.shape;
        if (spec.shape === sh) return;
        spec.shape = sh;
        const sz = SIZES[sh][0];
        const p = PRESETS[spec.preset];
        if (p.shape === sh) { spec.w = p.size[0]; spec.h = p.size[1]; }
        else if (Array.isArray(sz)) { spec.w = sz[0]; spec.h = sz[1]; } else { spec.w = sz; spec.h = sz; }
        spec.style.borderMm = sh === 'rect' || sh === 'square' ? 0.6 : 0.8;
        spec.style.orn.mm = ornWidthFor(sh);
        syncLook(); syncFrame(); drawFrameTiles(); changed('shape'); feature('shape', { shape: sh });
    }));
    el['sm-size'].addEventListener('change', () => {
        const v = el['sm-size'].value;
        el['sm-custom-size'].hidden = v !== 'custom';
        if (v !== 'custom') { const [w, h] = v.split('x').map(Number); spec.w = w; spec.h = h; changed('size'); }
    });
    const customSize = debounce(() => {
        const w = clamp(+el['sm-w'].value || spec.w, 8, 200), h = clamp(+el['sm-h'].value || spec.h, 8, 200);
        if (spec.shape === 'round' || spec.shape === 'square' || spec.shape === 'triangle') { spec.w = w; spec.h = w; el['sm-h'].value = w; }
        else { spec.w = w; spec.h = h; }
        changed('size');
    }, 250);
    el['sm-w'].addEventListener('input', customSize);
    el['sm-h'].addEventListener('input', customSize);
    buildFrame();
    // font
    fillFontSelect();
    el['sm-font'].addEventListener('change', () => { spec.style.font = el['sm-font'].value; changed('font'); feature('font', { id: spec.style.font.startsWith('custom') ? 'custom' : spec.style.font }); });
    // the picker previews each face in its own font: load them when it opens
    const warmFaces = () => { ensureCss(pickable().filter((f) => !f.face).map((f) => f.id), 700).catch(() => {}); };
    el['sm-font'].addEventListener('pointerdown', warmFaces, { once: true });
    el['sm-font'].addEventListener('focus', warmFaces, { once: true });
    el['sm-font-file'].addEventListener('change', async (e) => {
        const f = e.target.files && e.target.files[0]; e.target.value = '';
        if (!f) return;
        try {
            const entry = await registerCustom(f.name.replace(/\.[^.]+$/, ''), await f.arrayBuffer());
            fillFontSelect(); spec.style.font = entry.id; el['sm-font'].value = entry.id; changed('font'); feature('font_file');
        } catch (err) { toast(t('font_bad', 'This file is not a font the browser can read.'), 'error'); }
    });
    if (localFontsSupported()) {
        el['sm-font-local'].hidden = false;
        el['sm-font-local'].addEventListener('click', openLocalFonts);
    }
    el['sm-upper'].addEventListener('change', () => { spec.style.upper = el['sm-upper'].checked; changed('upper'); });
    // inks
    INKS.forEach((ink) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'sm-ink'; b.style.setProperty('--ink', ink.hex); b.dataset.hex = ink.hex; b.title = t('ink.' + ink.id, ink.id); b.setAttribute('aria-label', b.title);
        b.addEventListener('click', () => setColor(ink.hex));
        el['sm-inks'].appendChild(b);
    });
    el['sm-ink-custom'].addEventListener('input', () => setColor(el['sm-ink-custom'].value));
    // separators
    SEPARATORS.forEach((s) => { const o = document.createElement('option'); o.value = s; o.textContent = s; el['sm-sep'].appendChild(o); });
    const none = document.createElement('option'); none.value = ''; none.textContent = t('sep_none', '—'); el['sm-sep'].appendChild(none);
    el['sm-sep'].addEventListener('change', () => { spec.style.sep = el['sm-sep'].value; changed('sep'); });
    // emblems
    const noneBtn = document.createElement('button');
    noneBtn.type = 'button'; noneBtn.className = 'sm-emblem sm-emblem-none'; noneBtn.dataset.emblem = ''; noneBtn.textContent = t('emblem.none', 'None'); noneBtn.title = noneBtn.textContent;
    noneBtn.addEventListener('click', () => setEmblem(null));
    el['sm-emblems'].appendChild(noneBtn);
    Object.keys(EMBLEMS).forEach((k) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'sm-emblem'; b.dataset.emblem = k; b.title = t('emblem.' + k, k); b.setAttribute('aria-label', b.title);
        b.innerHTML = '<svg viewBox="0 0 100 100" aria-hidden="true"><path d="' + EMBLEMS[k] + '"/></svg>';
        b.addEventListener('click', () => setEmblem(k));
        el['sm-emblems'].appendChild(b);
    });
    el['sm-emblem-mm'].addEventListener('input', () => { spec.style.emblemMm = +el['sm-emblem-mm'].value; if (spec.style.logo) spec.style.logo.mm = spec.style.emblemMm; if (spec.style.qr) spec.style.qr.mm = spec.style.emblemMm; changed('emblem_mm'); });
    // logo
    bindFile(el['sm-logo-drop'], (f) => loadLogo(f));
    el['sm-logo-drop'].addEventListener('tool-uploader:clear', () => { if (spec.style.logo) { spec.style.logo = null; syncLook(); changed('logo'); } });
    el['sm-logo-thr'].addEventListener('input', debounce(async () => { if (!spec.style.logo) return; spec.style.logo.threshold = +el['sm-logo-thr'].value; await rebuildLogo(); changed('logo'); }, 120));
    el['sm-logo-clear'].addEventListener('click', () => { spec.style.logo = null; syncLook(); changed('logo'); });
    // qr
    el['sm-qr'].addEventListener('input', debounce(() => {
        const v = el['sm-qr'].value.trim();
        spec.style.qr = v ? { data: v, mm: spec.style.emblemMm || 10 } : null;
        changed('qr'); if (v) feature('qr');
    }, 300));
    // the security mesh behind the centre
    ['none'].concat(MESHES).forEach((k) => { const o = document.createElement('option'); o.value = k; o.textContent = t('mesh_kind.' + k, k); el['sm-mesh'].appendChild(o); });
    el['sm-mesh'].addEventListener('change', () => { spec.style.mesh = el['sm-mesh'].value; syncLook(); changed('mesh'); feature('mesh', { id: spec.style.mesh }); });
    el['sm-mesh-density'].addEventListener('input', () => { spec.style.meshDensity = +el['sm-mesh-density'].value; setOut('sm-mesh-density', '×' + (+el['sm-mesh-density'].value).toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 })); changed('mesh'); });
    // fine tuning
    el['sm-tune-ring'].addEventListener('input', () => { spec.style.tune.ring = +el['sm-tune-ring'].value; changed('tune'); });
    el['sm-tune-center'].addEventListener('input', () => { spec.style.tune.center = +el['sm-tune-center'].value; changed('tune'); });
    el['sm-tune-dy'].addEventListener('input', () => { spec.style.tune.centerDy = +el['sm-tune-dy'].value; changed('tune'); });
    el['sm-track'].addEventListener('input', () => { spec.style.track = +el['sm-track'].value; changed('tune'); });
    el['sm-align'].addEventListener('change', () => { spec.style.align = el['sm-align'].value; changed('align'); });
    if (el['sm-sample-clear']) el['sm-sample-clear'].addEventListener('click', clearSample);
}

function fillFontSelect() {
    const sel = el['sm-font'];
    const cur = spec ? spec.style.font : 'tinos';
    sel.innerHTML = '';
    const texts = spec ? textsOf(zonesOf(spec)).join(' ') : '';
    const scripts = scriptsIn(texts);
    pickable().forEach((f) => {
        const o = document.createElement('option');
        o.value = f.id; o.textContent = f.name; o.style.fontFamily = '"' + f.family + '", sans-serif';
        const ok = f.scripts.includes('*') || Array.from(scripts).every((s) => f.scripts.includes(s) || !['latin', 'cyrillic', 'greek'].includes(s));
        if (!ok) { o.disabled = true; o.textContent = f.name + ' · ' + t('font_no_script', 'no letters for this text'); }
        sel.appendChild(o);
    });
    sel.value = cur;
    if (sel.value !== cur) { sel.value = 'tinos'; if (spec) spec.style.font = 'tinos'; }
}

function setColor(hex) {
    spec.style.color = hex;
    el['sm-inks'].querySelectorAll('.sm-ink').forEach((b) => b.setAttribute('aria-pressed', b.dataset.hex.toLowerCase() === hex.toLowerCase() ? 'true' : 'false'));
    el['sm-ink-custom'].value = hex;
    drawFrameTiles();
    if (spec.style.logo) rebuildLogo().then(() => changed('color')); else changed('color');
}

function setEmblem(k) {
    spec.style.emblem = k;
    if (k) { spec.style.qr = null; el['sm-qr'].value = ''; }
    el['sm-emblems'].querySelectorAll('.sm-emblem').forEach((b) => b.setAttribute('aria-pressed', (b.dataset.emblem || null) === k ? 'true' : 'false'));
    changed('emblem'); if (k) feature('emblem', { id: k });
}

async function loadLogo(file) {
    try {
        const raw = await readAsDataURL(file);
        const svg = /svg/i.test(file.type) || /\.svg$/i.test(file.name);
        // a raster logo is kept as the stamp reads it — ≤ 900 px, grey (util.js compactLogo),
        // not the photo: the whole file went into the draft, every undo step, every saved design
        const src = svg ? raw : compactLogo(await loadImage(raw), 900);
        spec.style.logo = { src, gray: svg ? 0 : 1, mm: spec.style.emblemMm || 12, threshold: +el['sm-logo-thr'].value || 128 };
        spec.style.emblem = null; spec.style.qr = null; el['sm-qr'].value = '';
        await rebuildLogo();
        syncLook(); changed('logo');
        feature('logo', { type: svg ? 'svg' : 'raster', kb: Math.round(file.size / 1024), kept_kb: Math.round(src.length * 0.75 / 1024) });
        toast(t('logo_added', 'Logo added'), 'success');
    } catch (e) { fail(e, 'logo'); toast(t('logo_bad', 'Could not read that image.'), 'error'); }
}

// A draft, a saved design or a link from before 01.10.2026 carries the whole photo:
// made small the first time it is drawn, and the draft rewritten with it.
const LOGO_COMPACT_FROM = 150000;
async function rebuildLogo() {
    const L = spec.style.logo;
    if (!L || !L.src) return;
    let img = await loadImage(L.src);
    if (!L.gray && L.src.length > LOGO_COMPACT_FROM && !/^data:image\/svg/i.test(L.src)) {
        const before = L.src.length;
        L.src = compactLogo(img, 900); L.gray = 1;
        img = await loadImage(L.src);
        try { saveDraft(); } catch (e) {}
        feature('logo_compacted', { from_kb: Math.round(before * 0.75 / 1024), to_kb: Math.round(L.src.length * 0.75 / 1024) });
    }
    L.img = thresholdImage(img, L.threshold, spec.style.color, 900);
}

/* ── frame: the rim, its pattern, the two inner rings ──────────────────── */
const fmtMm = (v) => (+v).toLocaleString(LOCALE, { maximumFractionDigits: 2 }) + ' ' + t('mm', 'mm');
const setOut = (id, text) => { const o = el[id + '-out']; if (o) o.textContent = text; };
const cap1 = (s) => { s = String(s || ''); return s ? s.charAt(0).toLocaleUpperCase(LOCALE) + s.slice(1) : s; };
// a ring is «кольцо», not «рамка»: the ring names are nouns of their own where
// the border adjective would not agree («точечная» → «точки»)
const ringLabel = (k) => cap1(lookup('ring.' + k) != null ? t('ring.' + k, k) : t('border.' + k, k));
const frameShape = () => (spec && (spec.shape === 'rect' || spec.shape === 'square' || spec.shape === 'triangle')) ? 'frame' : 'round';

function buildFrame() {
    const host = el['sm-frames'];
    host.innerHTML = '';
    BORDERS.forEach((kind) => {
        const b = document.createElement('button');
        const label = cap1(t('border.' + kind, kind));
        b.type = 'button'; b.className = 'sm-frame'; b.dataset.border = kind; b.title = label;
        b.setAttribute('aria-pressed', 'false');
        b.innerHTML = '<canvas width="128" height="60" aria-hidden="true"></canvas><span>' + esc(label) + '</span>';
        b.addEventListener('click', () => setBorder(kind));
        host.appendChild(b);
    });
    ['sm-mid', 'sm-core'].forEach((id) => {
        const sel = el[id];
        RING_KINDS.forEach((k) => { const o = document.createElement('option'); o.value = k; o.textContent = ringLabel(k); sel.appendChild(o); });
    });
    const num = (id, fn, fmt) => {
        el[id].addEventListener('input', () => { fn(+el[id].value); setOut(id, fmt(+el[id].value)); changed('border'); });
        el[id].addEventListener('change', () => feature('frame_tune', { k: id.replace('sm-', ''), v: +el[id].value }));
    };
    num('sm-border-mm', (v) => { spec.style.borderMm = v; }, fmtMm);
    num('sm-orn-mm', (v) => { spec.style.orn.mm = v; }, fmtMm);
    num('sm-orn-density', (v) => { spec.style.orn.density = v; }, (v) => '×' + v.toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    num('sm-orn-line', (v) => { spec.style.orn.line = v; }, fmtMm);
    num('sm-orn-strands', (v) => { spec.style.orn.strands = v; }, (v) => String(v));
    num('sm-ring-orn-mm', (v) => { spec.style.ringOrnMm = v; }, fmtMm);
    el['sm-orn-edges'].addEventListener('change', () => { spec.style.orn.edges = el['sm-orn-edges'].checked; changed('border'); });
    el['sm-micro'].addEventListener('change', () => {
        const st = spec.style;
        st.micro = el['sm-micro'].checked;
        if (st.micro && !(st.microtext && st.microtext.trim())) st.microtext = t('microtext_default', 'VERIFIED • ORIGINAL • ');
        syncFrame(); changed('micro'); feature('micro', { on: st.micro ? 1 : 0 });
    });
    el['sm-microtext'].addEventListener('input', () => { spec.style.microtext = el['sm-microtext'].value; changed('microtext'); });
    el['sm-mid'].addEventListener('change', () => { spec.style.mid = el['sm-mid'].value; syncFrame(); changed('ring'); feature('ring', { slot: 'mid', id: spec.style.mid }); });
    el['sm-core'].addEventListener('change', () => { spec.style.core = el['sm-core'].value; syncFrame(); changed('ring'); feature('ring', { slot: 'core', id: spec.style.core }); });
}

function setBorder(kind) {
    const st = spec.style;
    if (st.border === kind) return;
    st.border = kind;
    // a pattern starts from its own stroke and strand count, not the last one's
    if (isOrnament(kind)) { st.orn.line = null; st.orn.strands = null; }
    syncFrame(); changed('border'); feature('border', { id: kind, shape: spec.shape });
}

function syncFrame() {
    const st = spec.style, kind = st.border, orn = isOrnament(kind), pat = PATTERNS[kind] || {};
    el['sm-frames'].querySelectorAll('.sm-frame').forEach((b) => b.setAttribute('aria-pressed', b.dataset.border === kind ? 'true' : 'false'));
    el['sm-border-mm-row'].hidden = orn || kind === 'none';
    el['sm-border-mm'].value = st.borderMm; setOut('sm-border-mm', fmtMm(st.borderMm));
    ['sm-orn-mm-row', 'sm-orn-density-row', 'sm-orn-line-row', 'sm-orn-edges-row'].forEach((id) => { el[id].hidden = !orn; });
    el['sm-orn-strands-row'].hidden = !(orn && pat.strands);
    const line = st.orn.line || pat.line || 0.16, strands = st.orn.strands || pat.strands || 1;
    el['sm-orn-mm'].value = st.orn.mm; setOut('sm-orn-mm', fmtMm(st.orn.mm));
    el['sm-orn-density'].value = st.orn.density || 1; setOut('sm-orn-density', '×' + (+(st.orn.density || 1)).toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    el['sm-orn-line'].value = line; setOut('sm-orn-line', fmtMm(line));
    el['sm-orn-strands'].value = strands; setOut('sm-orn-strands', String(strands));
    el['sm-orn-edges'].checked = st.orn.edges !== false;
    el['sm-micro'].checked = !!st.micro;
    el['sm-microtext-row'].hidden = !st.micro;
    el['sm-microtext'].value = st.microtext || '';
    el['sm-mid'].value = st.mid || 'auto';
    el['sm-core'].value = st.core || 'auto';
    el['sm-mid-label'].textContent = t(frameShape() === 'frame' ? 'ring_mid_frame' : 'ring_mid_round', el['sm-mid-label'].textContent);
    el['sm-ring-orn-row'].hidden = !(isOrnament(st.mid) || isOrnament(st.core));
    el['sm-ring-orn-mm'].value = st.ringOrnMm || 1.4; setOut('sm-ring-orn-mm', fmtMm(st.ringOrnMm || 1.4));
}

/* The tiles are drawn by the same engine as the stamp: an arc of a 40 mm rim
 * for round dies, a straight strip for the others — what you pick is what
 * the edge of YOUR stamp will carry. */
function drawFrameTiles() {
    if (!spec || !el['sm-frames']) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const straight = frameShape() === 'frame';
    el['sm-frames'].querySelectorAll('.sm-frame').forEach((b) => {
        const cv = b.querySelector('canvas'), kind = b.dataset.border;
        const cssW = 64, cssH = 30;
        cv.width = cssW * dpr; cv.height = cssH * dpr;
        const ctx = cv.getContext('2d');
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, cv.width, cv.height);
        const ppm = 7 * dpr;
        ctx.scale(ppm, ppm);
        const vw = cssW * dpr / ppm, vh = cssH * dpr / ppm, R = 20;
        // the band sits in the middle of the tile: its top edge is lowered by
        // half of what the tile has left once the band and the arc's sag are in
        const W = ornWidthFor(straight ? 'rect' : 'round') + (straight ? 0.4 : 0);
        const sag = straight ? 0 : R - Math.sqrt(R * R - (vw / 2) * (vw / 2));
        const top = Math.max(0.3, (vh - W - sag) / 2);
        const items = frameSample(kind, vw / 2, straight ? top : R + top, R, straight, vw, W);
        paintScene(ctx, { items, color: spec.style.color });
    });
}

function frameSample(kind, cx, cy, R, straight, vw, W) {
    const car = (d) => straight ? { L: vw + 4, closed: false, at: (s) => ({ x: s - 2, y: cy + d, nx: 0, ny: 1 }) } : circleCarrier(cx, cy, R - d);
    const line = (d, sw) => straight ? { t: 'stroke', d: 'M-1 ' + (cy + d) + 'L' + (vw + 1) + ' ' + (cy + d), sw } : { t: 'ring', cx, cy, r: R - d, sw };
    if (isOrnament(kind)) return ornamentItems(car(W / 2), { kind, W, edges: true });
    if (kind === 'dotted' || kind === 'beaded' || kind === 'dashed') { const sw = 0.8, dia = kind === 'beaded' ? sw * 1.2 : sw; return spacedItems(car(dia / 2), kind, sw); }
    if (kind === 'single') return [line(0.4, 0.8)];
    if (kind === 'double') return [line(0.4, 0.8), line(1.55, 0.3)];
    if (kind === 'triple') return [line(0.4, 0.8), line(1.55, 0.3), line(2.4, 0.25)];
    return [];
}

/* ── ink (realism) ─────────────────────────────────────────────────────── */
function buildInk() {
    const out = (id, v, suffix) => { const o = el[id + '-out']; if (o) o.textContent = v + (suffix || ''); };
    el['sm-realism'].addEventListener('input', () => { spec.ink.realism = +el['sm-realism'].value; out('sm-realism', spec.ink.realism, '%'); changed('ink', true); });
    el['sm-spread'].addEventListener('input', () => { spec.ink.spread = +el['sm-spread'].value; out('sm-spread', spec.ink.spread, '%'); changed('ink', true); });
    el['sm-rough'].addEventListener('input', () => { spec.ink.rough = +el['sm-rough'].value; out('sm-rough', spec.ink.rough, '%'); changed('ink', true); });
    el['sm-smear'].addEventListener('input', () => { spec.ink.smear = +el['sm-smear'].value; out('sm-smear', spec.ink.smear, '%'); changed('ink', true); });
    el['sm-smear'].addEventListener('change', () => feature('smear', { v: spec.ink.smear }));
    el['sm-tilt'].addEventListener('change', () => { spec.ink.tilt = el['sm-tilt'].checked ? 1 : 0; changed('ink', true); });
    el['sm-reroll'].addEventListener('click', () => { spec.ink.seed = (Math.random() * 0xffffffff) >>> 0; changed('ink', true); feature('reroll'); });
    el['sm-paper'].addEventListener('change', () => { spec.ink.paper = el['sm-paper'].value; syncPaper(); changed('paper', true); });
    el['sm-realism'].addEventListener('change', () => feature('realism', { v: spec.ink.realism }));
}

function syncPaper() {
    el['sm-preview'].dataset.paper = spec.ink.paper || 'white';
}

/* ── sync form ← spec ──────────────────────────────────────────────────── */
function syncAll() {
    markPreset();
    buildFields();
    syncLook();
    syncFrame();
    drawFrameTiles();
    syncInk();
    syncPaper();
    fillFontSelect();
    syncSummaries();
}

function syncLook() {
    const st = spec.style;
    el['sm-shape'].querySelectorAll('[data-shape]').forEach((b) => b.setAttribute('aria-pressed', b.dataset.shape === spec.shape ? 'true' : 'false'));
    // sizes for the shape
    const sel = el['sm-size'];
    sel.innerHTML = '';
    const list = SIZES[spec.shape] || SIZES.round;
    let matched = false;
    list.forEach((s) => {
        const [w, h] = Array.isArray(s) ? s : [s, s];
        const o = document.createElement('option'); o.value = w + 'x' + h;
        o.textContent = Array.isArray(s) ? w + '×' + h + ' ' + t('mm', 'mm') : w + ' ' + t('mm', 'mm');
        if (w === spec.w && h === spec.h) { o.selected = true; matched = true; }
        sel.appendChild(o);
    });
    const c = document.createElement('option'); c.value = 'custom'; c.textContent = t('size_custom', 'Custom…'); sel.appendChild(c);
    if (!matched) sel.value = 'custom';
    el['sm-custom-size'].hidden = matched;
    el['sm-w'].value = spec.w; el['sm-h'].value = spec.h;
    el['sm-h'].disabled = spec.shape === 'round' || spec.shape === 'square' || spec.shape === 'triangle';
    el['sm-font'].value = st.font;
    el['sm-upper'].checked = !!st.upper;
    el['sm-inks'].querySelectorAll('.sm-ink').forEach((b) => b.setAttribute('aria-pressed', b.dataset.hex.toLowerCase() === st.color.toLowerCase() ? 'true' : 'false'));
    el['sm-ink-custom'].value = st.color;
    el['sm-sep'].value = st.sep || '';
    const ringy = spec.shape !== 'rect' && spec.shape !== 'square';
    el['sm-sep'].closest('.sm-field').hidden = !ringy;
    el['sm-emblems'].querySelectorAll('.sm-emblem').forEach((b) => b.setAttribute('aria-pressed', (b.dataset.emblem || null) === st.emblem ? 'true' : 'false'));
    el['sm-emblem-mm'].value = st.emblemMm;
    el['sm-emblem-row'].hidden = !(st.emblem || st.logo || st.qr);
    el['sm-logo-row'].hidden = !st.logo;
    if (st.logo) el['sm-logo-thr'].value = st.logo.threshold;
    el['sm-qr'].value = st.qr ? st.qr.data : '';
    el['sm-tune-ring'].value = st.tune.ring; el['sm-tune-center'].value = st.tune.center; el['sm-tune-dy'].value = st.tune.centerDy; el['sm-track'].value = st.track || 0;
    el['sm-align'].value = st.align || 'center';
    el['sm-align-row'].hidden = ringy;
    el['sm-mesh'].value = st.mesh || 'none';
    el['sm-mesh-density-row'].hidden = !MESHES.includes(st.mesh);
    el['sm-mesh-density'].value = st.meshDensity || 1;
    setOut('sm-mesh-density', '×' + (+(st.meshDensity || 1)).toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    if (st.logo && st.logo.src && !st.logo.img) rebuildLogo().then(() => render());
}

function syncInk() {
    el['sm-realism'].value = spec.ink.realism; el['sm-spread'].value = spec.ink.spread; el['sm-rough'].value = spec.ink.rough == null ? spec.ink.realism : spec.ink.rough;
    el['sm-smear'].value = spec.ink.smear || 0;
    el['sm-tilt'].checked = !!spec.ink.tilt; el['sm-paper'].value = spec.ink.paper || 'white';
    ['sm-realism', 'sm-spread', 'sm-rough', 'sm-smear'].forEach((id) => { const o = el[id + '-out']; if (o) o.textContent = el[id].value + '%'; });
}

function mainText() {
    const p = PRESETS[spec.preset];
    for (const f of p.fields) { const v = (spec.fields[f.id] || '').trim(); if (v && f.kind !== 'date') return v.split(/\r?\n/)[0]; }
    return '';
}

function syncSummaries() {
    const sizeStr = (spec.w === spec.h ? spec.w : spec.w + '×' + spec.h) + ' ' + t('mm', 'mm');
    el['sm-sum-data'].textContent = mainText().slice(0, 40);
    el['sm-sum-look'].textContent = [t('shape.' + spec.shape, spec.shape), sizeStr, fontById(spec.style.font).name].join(' · ');
    const st = spec.style;
    const frame = [t('border.' + st.border, st.border)];
    if (isOrnament(st.border)) frame.push(fmtMm(st.orn.mm));
    if (st.micro) frame.push(t('sum_micro', 'microtext'));
    el['sm-sum-frame'].textContent = frame.join(' · ');
    el['sm-sum-ink'].textContent = inkActive(spec.ink) ? tf('sum_realism', { n: spec.ink.realism }, 'impression :n%') : t('sum_crisp', 'clean print');
}

/* ── change → autosave → history → render ──────────────────────────────── */
const saveDraft = debounce(() => { try { window.__toolSettings && window.__toolSettings.save(SLUG, DRAFT_KEY, portable(spec)); } catch (e) {} }, 500);
const pushLater = debounce(() => pushHistory(), 600);

function changed(what, inkOnly) {
    syncSummaries();
    saveDraft();
    pushLater();
    render(inkOnly);
}

/* ── preview ───────────────────────────────────────────────────────────── */
let previewW = 420;
/* the CONTENT width of the preview box — its padding is not room for the stamp */
function previewRoom() {
    const pv = el['sm-preview'];
    const cs = getComputedStyle(pv);
    return Math.max(0, pv.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0));
}
function wirePreview() {
    const onRoom = debounce(() => { const w = previewRoom(); if (w && Math.abs(w - previewW) > 2) { previewW = w; render(); } }, 80);
    // Safari до 13.1 наблюдателя не знает: голый конструктор обрывал wirePreview()
    // и весь запуск после неё (ссылка #d=, черновик). Без него ширина превью
    // перемеряется по окну — второй слушатель ниже перерисовывает, но ширину не берёт.
    if (typeof ResizeObserver === 'function') new ResizeObserver(onRoom).observe(el['sm-preview']);
    else window.addEventListener('resize', onRoom);
    previewW = previewRoom() || 420;
    window.addEventListener('resize', debounce(() => render(), 150));
    const cv = el['sm-canvas'];
    cv.addEventListener('click', (e) => {
        if (!scene) return;
        const r = cv.getBoundingClientRect();
        const mm = pxToMm(e.clientX - r.left, e.clientY - r.top, r);
        const zone = hitZone(scene, mm.x, mm.y);
        if (zone) focusZone(zone);
    });
    cv.addEventListener('mousemove', (e) => {
        if (!scene) return;
        const r = cv.getBoundingClientRect();
        const mm = pxToMm(e.clientX - r.left, e.clientY - r.top, r);
        const zone = hitZone(scene, mm.x, mm.y);
        cv.style.cursor = zone ? 'pointer' : 'default';
        const hint = el['sm-zone-hint'];
        if (hint) {
            const f = zone && zone !== 'border' ? fieldForZone(zone) : null;
            const text = zone === 'border' ? t('zone_border', 'Frame') : f ? t('f.' + f.label, f.id) : '';
            hint.textContent = text; hint.hidden = !text;
        }
    });
    cv.addEventListener('mouseleave', () => { const hint = el['sm-zone-hint']; if (hint) hint.hidden = true; });
}
let lastPad = 0;
function pxToMm(px, py, rect) {
    const k = ((scene ? scene.w : spec.w) + 2 * lastPad) / rect.width;
    return { x: px * k - lastPad, y: py * k - lastPad };
}

const ZONE_PREF = {
    ringOuter: ['name_full', 'fio', 'ring_top'],
    ringInner: ['ogrn', 'ogrnip', 'edrpou', 'ipn', 'ring_inner', 'inn', 'company_no', 'vat', 'hrb', 'npwp', 'credit_code'],
    arcBottom: ['city', 'ring_bottom', 'state', 'court', 'siret', 'cnpj', 'cif', 'cr', 'credit_code', 'nif'],
    center: ['name_short', 'center', 'specialty', 'title', 'year', 'commission', 'fio'],
    lines: ['lines', 'name_short', 'name_full'],
    side1: ['side1', 'name_short'], side2: ['side2', 'address'], side3: ['side3', 'phone'],
};
function fieldForZone(zone) {
    if (zone === 'art') return null;
    const p = PRESETS[spec.preset];
    const pref = ZONE_PREF[zone] || [];
    for (const id of pref) { const f = p.fields.find((x) => x.id === id); if (f) return { id: f.id, label: f.label || f.id }; }
    const f = p.fields.find((x) => x.kind !== 'date');
    return f ? { id: f.id, label: f.label || f.id } : null;
}
function focusZone(zone) {
    if (zone === 'art') { el['sm-sec-look'].open = true; el['sm-emblems'].scrollIntoView({ block: 'nearest' }); const b = el['sm-emblems'].querySelector('[aria-pressed="true"]') || el['sm-emblems'].firstElementChild; b && b.focus(); feature('zone_click', { zone }); return; }
    if (zone === 'border') { el['sm-sec-frame'].open = true; const b = el['sm-frames'].querySelector('[aria-pressed="true"]') || el['sm-frames'].firstElementChild; if (b) { b.scrollIntoView({ block: 'nearest' }); b.focus(); } feature('zone_click', { zone }); return; }
    const f = fieldForZone(zone);
    if (!f) return;
    el['sm-sec-data'].open = true;
    const input = el['sm-fields'].querySelector('[data-field="' + f.id + '"]');
    if (input) { input.focus(); input.scrollIntoView({ block: 'nearest' }); input.classList.remove('sm-flash'); void input.offsetWidth; input.classList.add('sm-flash'); }
    feature('zone_click', { zone });
}

/* How long a stamp waits on what it is drawn from. The preview draws with
 * what the browser has after a short wait and draws again when the rest
 * lands; an export waits longer and then refuses out loud — a file set in a
 * face the person did not choose, or missing the QR they typed, is not their
 * file. Until 2026-09-30 every one of these waits was unbounded (refund #37). */
const FONT_WAIT_PREVIEW = 8000, FONT_WAIT_EXPORT = 45000, QR_WAIT_PREVIEW = 1500, LOGO_WAIT = 15000;

async function prepare(s, o) {
    const exporting = !!(o && o.exporting);
    const zones = zonesOf(s);
    const chains = new Set();
    textsOf(zones).forEach((x) => chainFor(x, s.style.font).forEach((c) => chains.add(c)));
    if (s.style.micro && s.style.microtext) chainFor(s.style.microtext, s.style.font).forEach((c) => chains.add(c));
    if (s.style.sep) chainFor(s.style.sep, s.style.font).forEach((c) => chains.add(c));
    const faces = Array.from(chains);
    try { await bounded(ensureCss(faces, 700), exporting ? FONT_WAIT_EXPORT : FONT_WAIT_PREVIEW, 'fonts'); }
    catch (e) {
        if (!isWaitError(e)) diag('font_css_failed', { font: s.style.font });
        else if (exporting) { forgetPendingCss(); throw e; }
        else ensureCss(faces, 700).then(() => { if (s === spec) render(); }, () => {});
    }
    if (s.style.qr && s.style.qr.data && !s.style.qr.modules) {
        // the QR stays in the design when its library is late or missing; the
        // layout draws no code without modules. It used to be deleted silently.
        const q = s.style.qr;
        const want = qrMatrix(q.data, CFG).then((m) => { q.modules = m; });
        try { await (exporting ? want : bounded(want, QR_WAIT_PREVIEW, 'qr')); }
        catch (e) {
            if (exporting) throw isWaitError(e) ? e : waitError('qr', 0);
            if (isWaitError(e)) want.then(() => { if (s === spec) render(); }, () => diag('qr_failed'));
            else diag('qr_failed');
        }
    }
    if (s.style.logo && s.style.logo.src && !s.style.logo.img) {
        try { await bounded(rebuildLogoFor(s), LOGO_WAIT, 'logo'); }
        catch (e) { if (exporting) throw isWaitError(e) ? e : waitError('logo', 0); s.style.logo = null; }
    }
    return layout(s, zones);
}
async function rebuildLogoFor(s) { const img = await loadImage(s.style.logo.src); s.style.logo.img = thresholdImage(img, s.style.logo.threshold, s.style.color, 900); }

/* The preview is drawn into ONE reused offscreen canvas and copied into the
 * visible one, whose backing store is resized only when its size changes.
 * Until 2026-09-25 every keystroke allocated a fresh ~3 MB canvas and reset
 * the visible one; WebKit caps the memory of all canvases on a page, and on
 * iPhone typing a name took 5–13 s per key to paint (inp_slow, 15 sessions,
 * almost all iOS). The inked impression is computed once the typing pauses,
 * one job at a time: a burst of keys used to queue one full erosion per key. */
let previewOff = null, inkOut = null, view = { cssW: 0, pxPerMm: 0 };
let inkTimer = 0, inkBusy = false, inkWant = 0, shownInked = false;
const inkActive = (ink) => ink.realism > 0 || ink.spread > 0 || (ink.rough != null && ink.rough > 0) || ink.smear > 0;
const inkPad = (w) => Math.max(1.5, Math.min(4, w * 0.06));

async function render(inkOnly) {
    if (!spec) return;
    const seq = ++renderSeq;
    try {
        if (!inkOnly || !scene) scene = await prepare(spec);
        if (seq !== renderSeq) return;
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const inked = inkActive(spec.ink);
        const pad = inked ? inkPad(scene.w) : 0.4;
        lastPad = pad;
        const narrow = (el['sm'].clientWidth || 1000) < 720;
        const maxH = narrow ? window.innerHeight * 0.34 : Math.min(window.innerHeight * 0.46, 440);
        const aspect = (scene.w + 2 * pad) / (scene.h + 2 * pad);
        const cssW = Math.max(120, Math.min(previewW, Math.floor(maxH * aspect)));
        const pxPerMm = (cssW * dpr) / (scene.w + 2 * pad);
        previewOff = renderScene(scene, { pxPerMm, padMm: pad, bg: null, canvas: previewOff || undefined });
        view = { cssW, pxPerMm };
        // an ink-only change keeps the last impression up until the new one lands
        if (!(inkOnly && inked && shownInked)) showPreview(previewOff, cssW, false);
        updateMeta();
        syncRings();
        if (inked) scheduleInk(seq, inkOnly ? 40 : 170);
    } catch (e) { fail(e, 'render'); }
}

/* Width only: the height follows the canvas's own proportions (CSS height:auto).
 * An explicit height used to meet `max-width:100%` clamping the width to the
 * padded box, and every round stamp previewed as an ellipse ~8 % taller than
 * wide (measured 335×370 px on the 2026-09-25 build). */
function showPreview(src, cssW, inked) {
    const cv = el['sm-canvas'];
    if (cv.width !== src.width || cv.height !== src.height) { cv.width = src.width; cv.height = src.height; }
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.drawImage(src, 0, 0);
    const w = cssW + 'px';
    if (cv.style.width !== w) cv.style.width = w;
    if (cv.style.height) cv.style.height = '';
    shownInked = inked;
}

function scheduleInk(seq, delay) {
    clearTimeout(inkTimer);
    inkTimer = setTimeout(() => runInk(seq), delay);
}

async function runInk(seq) {
    if (seq !== renderSeq || !inkActive(spec.ink)) return;     // a newer render scheduled its own
    if (inkBusy) { inkWant = seq; return; }
    inkBusy = true;
    try {
        const v = view;
        const out = await compose(previewOff, spec.ink, v.pxPerMm, { target: inkOut, reuse: true });
        if (out !== previewOff) inkOut = out;
        if (seq === renderSeq) showPreview(out, v.cssW, true);
    } catch (e) { fail(e, 'ink'); }
    finally {
        inkBusy = false;
        if (inkWant) { const w = inkWant; inkWant = 0; if (w === renderSeq) runInk(w); }
    }
}

/* The ring under the inner text exists only when there IS inner text; and a
 * text the fit had to set below its legible size is said out loud — before
 * 2026-09-25 it silently ran past its arc and over the next line instead. */
let tightSaid = '';
function syncRings() {
    const has = !!(scene && scene.meta && scene.meta.hasInner);
    if (el['sm-core-row'].hidden === has) el['sm-core-row'].hidden = !has;
    const tightRoles = (scene && scene.meta && scene.meta.tight) || [];
    const note = el['sm-fit-note'];
    if (note) {
        note.hidden = !tightRoles.length;
        if (tightRoles.length) note.textContent = t('fit_tight', 'The text does not fit: some of it had to be set smaller than a stamp prints reliably. Make the stamp larger or shorten the text.');
    }
    const key = tightRoles.join(',') + '|' + spec.w + 'x' + spec.h;
    if (tightRoles.length && key !== tightSaid) { tightSaid = key; feature('fit_tight', { roles: tightRoles.join(','), w: spec.w, h: spec.h, preset: spec.preset }); }
}

function updateMeta() {
    const fmt = el['sm-format'].value, dpi = +el['sm-dpi'].value || 300;
    const die = dieSize(spec);
    const px = Math.round(die.w / 25.4 * dpi) + '×' + Math.round(die.h / 25.4 * dpi);
    const size = (spec.w === spec.h ? spec.w : spec.w + '×' + spec.h) + ' ' + t('mm', 'mm');
    el['sm-meta'].textContent = fmt === 'png' ? size + ' · ' + dpi + ' dpi · ' + px + ' px' : size + ' · ' + fmt.toUpperCase() + ' · ' + t('vector', 'vector');
    // the label only: textContent on the button used to wipe its download icon
    const lab = el['sm-download'].querySelector('span');
    (lab || el['sm-download']).textContent = tf('download_fmt', { fmt: fmt.toUpperCase() }, 'Download :fmt');
    el['sm-dpi'].closest('.sm-opt').hidden = fmt === 'svg';
    el['sm-transparent'].closest('.sm-opt').hidden = fmt !== 'png';
    el['sm-outlines'].closest('.sm-opt').hidden = fmt === 'png';
}

/* ── exports ───────────────────────────────────────────────────────────── */
function fileBase(ext) {
    const N = window.__outputName;
    const main = mainText();
    const stem = t('file_stem', 'stamp');
    const size = spec.w === spec.h ? spec.w + t('mm', 'mm') : spec.w + 'x' + spec.h + t('mm', 'mm');
    const base = [stem, main, size].filter(Boolean).join(' ');
    return N ? N.join(N.sanitize(base), ext) : base.replace(/[\\/:*?"<>|]/g, '') + '.' + ext;
}

async function rasterCanvas(dpi, transparent) {
    const pxPerMm = dpi / 25.4;
    const inked = inkActive(spec.ink);
    const pad = inked ? inkPad(spec.w) : 0.5;
    const sc = await prepare(spec, { exporting: true });
    let c = renderScene(sc, { pxPerMm, padMm: pad, bg: null });
    if (inked) c = await compose(c, spec.ink, pxPerMm);
    if (!transparent) {
        const bg = document.createElement('canvas'); bg.width = c.width; bg.height = c.height;
        const ctx = bg.getContext('2d'); ctx.fillStyle = paperColor(); ctx.fillRect(0, 0, bg.width, bg.height); ctx.drawImage(c, 0, 0);
        c = bg;
    }
    return c;
}
async function rasterBlob(dpi, transparent) {
    const c = await rasterCanvas(dpi, transparent);
    return { blob: await canvasBlob(c, 'image/png'), canvas: c, w: c.width, h: c.height };
}
function paperColor() { return spec.ink.paper === 'kraft' ? '#e6d5b1' : spec.ink.paper === 'scan' ? '#f4f1ea' : '#ffffff'; }

async function outlinesFor(sc) {
    const chains = new Set();
    for (const it of sc.items) if (it.font) it.font.chain.forEach((c) => chains.add(c));
    const faces = await outlineFaces(Array.from(chains), 700, CFG);
    let missing = [];
    for (const it of sc.items) {
        if (it.t === 'glyphs') for (const g of it.glyphs) missing = missing.concat(uncovered(g.s, faces));
        else if (it.t === 'text') missing = missing.concat(uncovered(it.text, faces));
    }
    missing = Array.from(new Set(missing));
    if (missing.length) toast(tf('missing_glyphs', { chars: missing.slice(0, 12).join(' ') }, 'No outline for: :chars'), 'warning');
    return faces;
}

async function svgBlob(outlines) {
    const sc = await prepare(spec, { exporting: true });
    let faces = null;
    if (outlines) {
        try { faces = await outlinesFor(sc); feature('outlines'); }
        catch (e) { diag('fontkit_failed', { m: String(e && e.message).slice(0, 80) }); toast(t('outlines_unavailable', 'Outlines are unavailable right now — the SVG keeps text as text.'), 'warning'); }
    }
    const svg = sceneToSvg(sc, { outlines: faces, padMm: 0.5, title: mainText() || t('file_stem', 'stamp') });
    return { blob: new Blob([svg], { type: 'image/svg+xml' }), text: svg };
}

async function pdfBlob(outlines) {
    const inked = inkActive(spec.ink);
    const sc = await prepare(spec, { exporting: true });
    let raster = null, faces = null;
    if (inked) { raster = await rasterCanvas(600, true); }
    else if (outlines) {
        try { faces = await outlinesFor(sc); }
        catch (e) { diag('fontkit_failed', { m: String(e && e.message).slice(0, 80) }); raster = await rasterCanvas(600, true); toast(t('outlines_unavailable_pdf', 'Vector text is unavailable right now — the PDF carries a 600-dpi image instead.'), 'warning'); }
    } else { raster = await rasterCanvas(600, true); }
    const pad = inked ? inkPad(spec.w) : 0.5;
    const bytes = await scenePdf(sc, { cfg: CFG, outlines: faces, padMm: pad, raster, title: mainText() || t('file_stem', 'stamp'), producer: CFG.brand || 'Timbrica' });
    return { blob: new Blob([bytes], { type: 'application/pdf' }), raster: !!raster };
}

/* Public for tests: a Blob of the current design in a format. */
async function exportBlob(fmt, o) {
    o = o || {};
    if (fmt === 'svg') return (await svgBlob(o.outlines !== false)).blob;
    if (fmt === 'pdf') return (await pdfBlob(o.outlines !== false)).blob;
    return (await rasterBlob(o.dpi || 300, o.transparent !== false)).blob;
}

function capKey() { return hash32(designKey(spec)); }

function wireExports() {
    ['png', 'svg', 'pdf'].forEach((f) => { const o = document.createElement('option'); o.value = f; o.textContent = f.toUpperCase(); el['sm-format'].appendChild(o); });
    [300, 600, 1200].forEach((d) => { const o = document.createElement('option'); o.value = d; o.textContent = d + ' dpi'; if (d === 300) o.selected = true; el['sm-dpi'].appendChild(o); });
    if (window.__toolSettings) { window.__toolSettings.bind(SLUG, el['sm-format'], 'format', 'png'); window.__toolSettings.bind(SLUG, el['sm-dpi'], 'dpi', '300'); }
    el['sm-format'].addEventListener('change', updateMeta);
    el['sm-dpi'].addEventListener('change', updateMeta);
    el['sm-download'].addEventListener('click', download);
    el['sm-copy'].addEventListener('click', copy);
    el['sm-share'].addEventListener('click', prepareShare);
    if (el['sm-share-copy']) el['sm-share-copy'].addEventListener('click', async () => {
        try { await navigator.clipboard.writeText(el['sm-share-url'].value); toast(t('link_copied', 'Link copied'), 'success'); feature('share', { via: 'link' }); }
        catch (e) { el['sm-share-url'].select(); toast(t('copy_fail', 'Copy is not available here — select the link and copy it.'), 'warning'); }
    });
    if (el['sm-share-native']) {
        el['sm-share-native'].hidden = !(navigator.share && navigator.canShare);
        el['sm-share-native'].addEventListener('click', shareNative);
    }
    el['sm-to-pdf'].addEventListener('click', toSignPdf);
    el['sm-project-dl'].addEventListener('click', downloadProject);
    el['sm-project-file'].addEventListener('change', async (e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) openProject(f); });
    el['sm-reset'].addEventListener('click', () => { spec = fresh(spec.preset); sampleOf = Object.assign({}, spec.fields); syncAll(); changed('reset'); });
    el['sm-save'].addEventListener('click', saveDesign);
    el['sm-saved-open'].addEventListener('click', () => { renderSaved(); el['sm-saved'].showModal(); });
    el['sm-batch-open'].addEventListener('click', () => { el['sm-batch'].showModal(); feature('batch_open'); });
    el['sm-doc-open'].addEventListener('click', () => { el['sm-doc'].showModal(); feature('doc_open'); });
    el['sm-legal-open'].addEventListener('click', () => el['sm-legal'].showModal());
    positionPopovers();
}

/* Busy says so on the button (its label span — textContent on the button
 * itself would wipe the icon); updateMeta() puts the format label back. */
function busy(btn, on, label) {
    if (!btn) return;
    btn.disabled = !!on; btn.dataset.busy = on ? '1' : '';
    if (on) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy');
    if (label) { const lab = btn.querySelector('span'); if (lab) lab.textContent = label; }
}

/* A ceiling over the whole export, above every wait inside it: an await
 * written tomorrow without its own deadline still cannot take the button. */
const EXPORT_WAIT = 150000;

async function download() {
    const fmt = el['sm-format'].value, dpi = +el['sm-dpi'].value || 300;
    const L = window.__toolLimit;
    const run = L ? L.begin(SLUG, [capKey()]) : null;
    if (L && !run) return;
    const btn = el['sm-download'];
    busy(btn, true, t('preparing', 'Preparing the file…'));
    stage('exporting', { fmt });
    const t0 = Date.now();
    try {
        const made = await bounded((async () => {
            if (fmt === 'svg') { const r = await svgBlob(el['sm-outlines'].checked); return { blob: r.blob, extra: { outlines: el['sm-outlines'].checked ? 1 : 0 } }; }
            if (fmt === 'pdf') { const r = await pdfBlob(el['sm-outlines'].checked); return { blob: r.blob, extra: { raster: r.raster ? 1 : 0 } }; }
            const r = await rasterBlob(dpi, el['sm-transparent'].checked);
            return { blob: r.blob, extra: { dpi, px: r.w, alpha: el['sm-transparent'].checked ? 1 : 0 } };
        })(), EXPORT_WAIT, 'export');
        if (await deliver(made.blob, fileBase(fmt))) {
            run && run.commit();
            done(fmt, Object.assign({ realism: spec.ink.realism, preset: spec.preset, shape: spec.shape, w: spec.w, h: spec.h, kb: Math.round(made.blob.size / 1024), export_ms: Date.now() - t0 }, made.extra));
        } else { run && run.abort(); stage('ready'); }
    } catch (e) { run && run.abort(); exportFailed(e, fmt, t0); }
    finally { busy(btn, false); updateMeta(); }
}

/* Delivery is the primitive's VERDICT, not the fact that it was called.
 * __downloadFile answers with a Promise, and `if (ok)` took any Promise for
 * «saved»: it spent the day's unit and said «Файл сохранён» for files the
 * browser had refused. */
async function deliver(blob, name) {
    if (!window.__downloadFile) return legacyDownload(blob, name);
    return (await window.__downloadFile(blob, name)) === true;
}

function legacyDownload(blob, name) {
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    toast(t('saved', 'File saved'), 'success');
    return true;
}

/* The delivery primitive already tells the person where the file went; a
 * second «Файл сохранён» from the tool only repeated it. */
function done(kind, extra) {
    try { window.__aeToolComplete && window.__aeToolComplete(Object.assign({ f: 'export', kind }, extra || {})); } catch (e) {}
}

/* An export that produced no file says so, names what did not arrive, and
 * leaves every button working. The beacon rides the consent-free channel
 * (stage → tool_stage_stalled / tool_stage_error): the visitors this happens
 * to are the ones whose analytics we never see — refund #37 had declined
 * cookies, and her only trace was one such beacon. */
const STALL_TEXT = {
    fonts: ['stall_fonts', 'The stamp’s font did not load, so the file was not made. Check the connection and try again.'],
    script: ['stall_script', 'Part of the maker needed for this format did not load. Try again, or choose PNG.'],
    qr: ['stall_script', 'Part of the maker needed for this format did not load. Try again, or choose PNG.'],
    encode: ['stall_encode', 'The browser did not hand over the stamp image. Try again; if it repeats, turn off anti-tracking (canvas) extensions or open the page in another browser.'],
};
function exportFailed(e, what, t0) {
    const phase = isWaitError(e) ? e.phase : 'error';
    const meta = { phase, fmt: what, ms: Date.now() - (t0 || Date.now()) };
    if (phase === 'error') meta.msg = String((e && e.message) || e).slice(0, 160);
    stage(phase === 'error' ? 'error' : 'stalled', meta);
    const tx = STALL_TEXT[phase];
    toast(tx ? t(tx[0], tx[1]) : t('export_failed', 'Export failed — try again.'), 'error');
    try { console.error('[stamp-maker] export', e); } catch (x) {}
}

async function copy() {
    const L = window.__toolLimit;
    const run = L ? L.begin(SLUG, [capKey()]) : null;
    if (L && !run) return;
    busy(el['sm-copy'], true);
    const t0 = Date.now();
    try {
        const png = (await bounded(rasterBlob(300, true), EXPORT_WAIT, 'export')).blob;
        const items = { 'image/png': png };
        let withSvg = false;
        try {
            if (typeof ClipboardItem !== 'undefined' && ClipboardItem.supports && ClipboardItem.supports('image/svg+xml')) {
                items['image/svg+xml'] = (await svgBlob(false)).blob; withSvg = true;
            }
        } catch (e) { /* PNG only */ }
        await navigator.clipboard.write([new ClipboardItem(items)]);
        run && run.commit();
        toast(t('copied', 'Copied — paste it into your document'), 'success');
        feature('copy', { svg: withSvg ? 1 : 0 });
        try { window.__aeToolComplete && window.__aeToolComplete({ f: 'copy', kind: 'png', preset: spec.preset }); } catch (e) {}
    } catch (e) {
        run && run.abort();
        // «copy is not available» only when it was the clipboard that said no
        if (isWaitError(e)) exportFailed(e, 'copy', t0);
        else toast(t('copy_fail', 'Copy is not available in this browser — download the PNG instead.'), 'warning');
    }
    finally { busy(el['sm-copy'], false); }
}

async function prepareShare() {
    try {
        const { str, droppedLogo } = await encodeSpec(spec, { maxLogo: 60000 });
        const u = new URL(location.href); u.hash = 'd=' + str; u.search = '';
        el['sm-share-url'].value = u.toString();
        if (droppedLogo) toast(t('link_logo_dropped', 'The link carries the design without the logo — a link is not a file.'), 'info');
    } catch (e) { fail(e, 'share'); }
}

async function shareNative() {
    try {
        const png = (await bounded(rasterBlob(300, true), EXPORT_WAIT, 'export')).blob;
        const file = new File([png], fileBase('png'), { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) { await navigator.share({ files: [file], title: mainText() }); feature('share', { via: 'native' }); }
        else { await navigator.share({ url: el['sm-share-url'].value, title: mainText() }); feature('share', { via: 'native_url' }); }
    } catch (e) {
        if (isWaitError(e)) exportFailed(e, 'share');
        else if (e && e.name !== 'AbortError') toast(t('share_fail', 'Sharing is not available here.'), 'warning');
    }
}

/* Hand the stamp to /sign-pdf: the PNG waits in localStorage for the next
 * ten minutes, the other tool picks it up when its PDF is open. */
async function toSignPdf() {
    try {
        const png = (await bounded(rasterBlob(300, true), EXPORT_WAIT, 'export')).blob;
        const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = () => rej(r.error); r.readAsDataURL(png); });
        localStorage.setItem('tmb.stamp.handoff', JSON.stringify({ png: dataUrl, ts: Date.now(), name: mainText() }));
        feature('to_signpdf');
        const w = window.open(CFG.signPdfUrl, '_blank');
        if (!w) location.assign(CFG.signPdfUrl);
    } catch (e) { exportFailed(e, 'signpdf'); }
}

/* ── project file & saved designs ──────────────────────────────────────── */
function downloadProject() {
    const blob = new Blob([JSON.stringify({ app: 'timbrica-stamp', v: 2, spec: portable(spec) }, null, 1)], { type: 'application/json' });
    const name = fileBase('tmbstamp.json');
    if (window.__downloadFile) window.__downloadFile(blob, name); else legacyDownload(blob, name);
    feature('project_save');
}
async function openProject(file) {
    // two visitors picked a PNG/JPG of a stamp here (prod 2026-09) and were told
    // «not a project» in red: a picture is not a design, but it has a use
    if (/^image\//.test(file.type || '') || /\.(png|jpe?g|webp|gif|svg|bmp|heic)$/i.test(file.name || '')) {
        toast(t('project_is_image', 'This is a picture, not a project file. A picture can go in the centre as a logo (Look → Logo), or try the finished stamp on a document.'), 'warning');
        feature('project_image');
        return;
    }
    try {
        const j = JSON.parse(await readAsText(file));
        const s = j && j.spec ? j.spec : j;
        if (!s || !PRESETS[s.preset]) throw new Error('not a stamp project');
        apply(s); feature('project_open');
    } catch (e) { toast(t('project_bad', 'This file is not a stamp project.'), 'error'); }
}
function apply(s) { spec = normalise(s); sampleOf = {}; syncAll(); pushHistory(); render(); }

const SAVED_KEY = 'timbrica.stampMaker.designs';
function savedList() { try { return JSON.parse(localStorage.getItem(SAVED_KEY) || '[]'); } catch (e) { return []; } }
function saveDesign() {
    const name = (prompt(t('save_prompt', 'Name this design'), mainText() || t('saved_default', 'Stamp')) || '').trim();
    if (!name) return;
    const list = savedList();
    list.unshift({ name, at: Date.now(), spec: portable(spec) });
    try { localStorage.setItem(SAVED_KEY, JSON.stringify(list.slice(0, 20))); toast(t('design_saved', 'Design saved on this device'), 'success'); feature('design_save'); }
    catch (e) { toast(t('save_full', 'No room left in the browser storage — download the project file instead.'), 'warning'); }
}
// Designs saved before 01.10.2026 hold whole photos as logos: made small once, so a new
// design still fits next to them (the list shares ~5.2M characters with the whole site).
async function compactSavedLogos() {
    const list = savedList();
    let touched = false;
    for (const d of list) {
        const L = d && d.spec && d.spec.style && d.spec.style.logo;
        if (!L || L.gray || !L.src || L.src.length <= LOGO_COMPACT_FROM || /^data:image\/svg/i.test(L.src)) continue;
        try { L.src = compactLogo(await loadImage(L.src), 900); L.gray = 1; touched = true; } catch (e) {}
    }
    if (touched) { try { localStorage.setItem(SAVED_KEY, JSON.stringify(list)); feature('logo_compacted', { saved: 1 }); } catch (e) {} }
}
function renderSaved() {
    const host = el['sm-saved-list'];
    const list = savedList();
    if (!list.length) { host.innerHTML = '<p class="sm-note">' + esc(t('saved_empty', 'Nothing saved yet.')) + '</p>'; return; }
    host.innerHTML = list.map((d, i) => '<div class="sm-saved-item"><span class="sm-saved-name">' + esc(d.name) + '</span><span class="sm-saved-date">' + new Date(d.at).toLocaleDateString(LOCALE) + '</span>'
        + '<button type="button" class="sm-btn sm-btn-sm" data-load="' + i + '">' + esc(t('load', 'Open')) + '</button><button type="button" class="sm-btn sm-btn-sm sm-btn-ghost" data-del="' + i + '" aria-label="' + esc(t('delete', 'Delete')) + '">✕</button></div>').join('');
    host.querySelectorAll('[data-load]').forEach((b) => b.addEventListener('click', () => { const d = savedList()[+b.dataset.load]; if (d) { apply(d.spec); el['sm-saved'].close(); feature('design_open'); } }));
    host.querySelectorAll('[data-del]').forEach((b) => b.addEventListener('click', () => { const l = savedList(); l.splice(+b.dataset.del, 1); localStorage.setItem(SAVED_KEY, JSON.stringify(l)); renderSaved(); }));
}

/* ── history ───────────────────────────────────────────────────────────── */
function pushHistory(initial) {
    const snap = JSON.stringify(portable(spec));
    if (history[histIdx] === snap) return;
    history = history.slice(0, histIdx + 1);
    history.push(snap);
    if (history.length > 60) history.shift();
    histIdx = history.length - 1;
    updateHistoryButtons();
}
function updateHistoryButtons() { el['sm-undo'].disabled = histIdx <= 0; el['sm-redo'].disabled = histIdx >= history.length - 1; }
function restore(i) { histIdx = i; spec = normalise(JSON.parse(history[i])); sampleOf = {}; syncAll(); saveDraft(); render(); updateHistoryButtons(); }
function wireHistory() {
    el['sm-undo'].addEventListener('click', () => { if (histIdx > 0) restore(histIdx - 1); });
    el['sm-redo'].addEventListener('click', () => { if (histIdx < history.length - 1) restore(histIdx + 1); });
    document.addEventListener('keydown', (e) => {
        const tag = (e.target.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) { e.preventDefault(); if (histIdx > 0) restore(histIdx - 1); }
        if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) { e.preventDefault(); if (histIdx < history.length - 1) restore(histIdx + 1); }
    });
}

/* ── popovers: anchored by CSS where the browser can, by JS elsewhere ───── */
function positionPopovers() {
    const anchored = CSS.supports && CSS.supports('position-anchor: --x');
    if (anchored) return;
    [['sm-more', 'sm-more-pop'], ['sm-share', 'sm-share-pop']].forEach(([btnId, popId]) => {
        const pop = el[popId], btn = el[btnId];
        if (!pop || !btn) return;
        pop.addEventListener('toggle', (e) => {
            if (e.newState !== 'open') return;
            const r = btn.getBoundingClientRect();
            pop.style.position = 'fixed';
            pop.style.top = Math.min(window.innerHeight - pop.offsetHeight - 8, r.bottom + 6) + 'px';
            pop.style.left = Math.max(8, Math.min(window.innerWidth - pop.offsetWidth - 8, r.right - pop.offsetWidth)) + 'px';
        });
    });
}

/* ── dialogs: document preview, batch, local fonts ────────────────────── */
function wireDialogs() {
    document.querySelectorAll('dialog .sm-close').forEach((b) => b.addEventListener('click', () => b.closest('dialog').close()));
    // light dismiss for browsers without closedby="any"
    document.querySelectorAll('dialog.sm-dialog').forEach((d) => d.addEventListener('click', (e) => { if (e.target === d) d.close(); }));
    wireDoc();
    wireBatch();
    if (el['sm-local-filter']) el['sm-local-filter'].addEventListener('input', filterLocal);
    if (el['sm-local-use']) el['sm-local-use'].addEventListener('click', async () => {
        const opt = el['sm-local-list'].selectedOptions[0];
        if (!opt || !localEntries) return;
        const entry = localEntries.find((x) => x.family === opt.value);
        if (!entry) return;
        try { const f = await useLocal(entry); fillFontSelect(); spec.style.font = f.id; el['sm-font'].value = f.id; changed('font'); feature('font_local'); el['sm-local'].close(); }
        catch (e) { toast(t('font_bad', 'This file is not a font the browser can read.'), 'error'); }
    });
}

let localEntries = null;
async function openLocalFonts() {
    try {
        localEntries = await queryLocal();
        if (!localEntries || !localEntries.length) { toast(t('local_none', 'No installed fonts were offered by the browser.'), 'warning'); return; }
        filterLocal();
        el['sm-local'].showModal();
    } catch (e) { toast(t('local_denied', 'Permission to read installed fonts was not granted.'), 'warning'); }
}
function filterLocal() {
    const q = (el['sm-local-filter'].value || '').toLowerCase();
    const list = el['sm-local-list'];
    list.innerHTML = '';
    (localEntries || []).filter((x) => !q || x.family.toLowerCase().includes(q)).slice(0, 400).forEach((x) => {
        const o = document.createElement('option'); o.value = x.family; o.textContent = x.family; o.style.fontFamily = '"' + x.family + '"'; list.appendChild(o);
    });
    if (list.options.length) list.selectedIndex = 0;
}

/* document preview: the stamp on a scan, multiply-blended, draggable */
let docImg = null;
const docPos = { fx: 0.68, fy: 0.7, drag: false };
function wireDoc() {
    bindFile(el['sm-doc-drop'], async (f) => {
        try { docImg = await loadImage(await readAsDataURL(f)); docPos.fx = 0.68; docPos.fy = 0.7; el['sm-doc-stage'].hidden = false; redrawDoc(); feature('doc_preview'); }
        catch (err) { toast(t('logo_bad', 'Could not read that image.'), 'error'); }
    });
    el['sm-doc-drop'].addEventListener('tool-uploader:clear', () => { docImg = null; el['sm-doc-stage'].hidden = true; });
    ['sm-doc-size', 'sm-doc-rot', 'sm-doc-op'].forEach((id) => el[id].addEventListener('input', () => redrawDoc()));
    const cv = el['sm-doc-canvas'];
    const pos = (e) => { const r = cv.getBoundingClientRect(); const p = e.touches ? e.touches[0] : e; return { fx: clamp((p.clientX - r.left) / r.width, 0, 1), fy: clamp((p.clientY - r.top) / r.height, 0, 1) }; };
    cv.addEventListener('pointerdown', (e) => { docPos.drag = true; Object.assign(docPos, pos(e)); redrawDoc(); cv.setPointerCapture(e.pointerId); });
    cv.addEventListener('pointermove', (e) => { if (docPos.drag) { Object.assign(docPos, pos(e)); redrawDoc(); } });
    cv.addEventListener('pointerup', () => { docPos.drag = false; });
    el['sm-doc-dl'].addEventListener('click', async () => {
        if (!docImg) return;
        const L = window.__toolLimit;
        const run = L ? L.begin(SLUG, [capKey()]) : null;
        if (L && !run) return;
        const btn = el['sm-doc-dl'];
        busy(btn, true);
        const t0 = Date.now();
        try {
            await bounded(drawDoc(true), EXPORT_WAIT, 'export');
            const blob = await canvasBlob(cv, 'image/png');
            if (await deliver(blob, fileBase('doc.png'))) { run && run.commit(); done('doc', { preset: spec.preset }); } else run && run.abort();
        } catch (e) { run && run.abort(); exportFailed(e, 'doc', t0); }
        finally { busy(btn, false); }
    });
}
let docStamp = null, docStampKey = '';
// the live preview is not awaited: a stamp that cannot be drawn just leaves the last one
const redrawDoc = () => { drawDoc().catch(() => {}); };
async function drawDoc(full) {
    if (!docImg) return;
    const cv = el['sm-doc-canvas'], ctx = cv.getContext('2d');
    const maxW = full ? 2400 : 1200;
    const k = Math.min(1, maxW / docImg.naturalWidth);
    cv.width = Math.round(docImg.naturalWidth * k); cv.height = Math.round(docImg.naturalHeight * k);
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.drawImage(docImg, 0, 0, cv.width, cv.height);
    const key = JSON.stringify(portable(spec)) + (full ? 'F' : 'p');
    if (!docStamp || docStampKey !== key) { docStamp = await rasterCanvas(full ? 600 : 300, true); docStampKey = key; }
    const sizeFrac = (+el['sm-doc-size'].value || 25) / 100;
    const size = Math.min(cv.width, cv.height) * sizeFrac;
    const w = size, h = size * docStamp.height / docStamp.width;
    ctx.save();
    ctx.translate(cv.width * docPos.fx, cv.height * docPos.fy);
    ctx.rotate((+el['sm-doc-rot'].value || 0) * Math.PI / 180);
    ctx.globalAlpha = (+el['sm-doc-op'].value || 90) / 100;
    ctx.globalCompositeOperation = 'multiply';
    ctx.drawImage(docStamp, -w / 2, -h / 2, w, h);
    ctx.restore();
}

/* batch: a CSV → one stamp per row, through the shared delivery ladder */
let batchRows = null, batchCols = [];
function wireBatch() {
    bindFile(el['sm-batch-drop'], async (f) => {
        try {
            const { headers, rows } = parseCSV(await readAsText(f));
            batchRows = rows; batchCols = headers;
            const p = PRESETS[spec.preset];
            const matched = headers.filter((h) => p.fields.some((x) => x.id === h));
            const usedInText = headers.filter((h) => Object.values(spec.fields).some((v) => String(v || '').includes('{{' + h + '}}')));
            const info = el['sm-batch-info'];
            if (!rows.length) info.textContent = t('batch_empty', 'The table has no rows.');
            else if (!matched.length && !usedInText.length) info.textContent = tf('batch_no_match', { cols: headers.join(', '), fields: p.fields.filter((x) => x.kind !== 'date').map((x) => x.id).join(', ') }, 'No column matches a field. Columns: :cols. Fields: :fields');
            else info.textContent = tf('batch_rows', { n: rows.length, cols: matched.concat(usedInText).join(', ') }, ':n rows · columns used: :cols');
            el['sm-batch-run'].disabled = !rows.length || (!matched.length && !usedInText.length);
        } catch (err) { toast(t('batch_bad', 'Could not read the table.'), 'error'); }
    });
    el['sm-batch-example'].addEventListener('click', () => {
        const p = PRESETS[spec.preset];
        const cols = p.fields.filter((x) => x.kind !== 'date').map((x) => x.id);
        const csv = cols.join(',') + '\n' + [1, 2, 3].map((i) => cols.map((c) => '"' + String(spec.fields[c] || '').replace(/"/g, '""').split('\n')[0] + (c === cols[0] ? ' ' + i : '') + '"').join(',')).join('\n') + '\n';
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv' });
        if (window.__downloadFile) window.__downloadFile(blob, 'stamps-example.csv'); else legacyDownload(blob, 'stamps-example.csv');
    });
    el['sm-batch-run'].addEventListener('click', runBatch);
}

function rowSpec(row) {
    const s = JSON.parse(JSON.stringify(portable(spec)));
    for (const k in s.fields) {
        let v = String(s.fields[k] || '');
        if (row[k] != null && row[k] !== '') v = row[k];
        v = v.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (m, c) => (row[c] != null ? row[c] : m));
        s.fields[k] = v;
    }
    s.style.logo = spec.style.logo; s.style.qr = spec.style.qr ? Object.assign({}, spec.style.qr) : null;
    return s;
}

async function runBatch() {
    if (!batchRows || !batchRows.length) return;
    const fmt = el['sm-batch-fmt'].value || 'png';
    const rows = batchRows.slice(0, 500);
    const specs = rows.map(rowSpec);
    const L = window.__toolLimit;
    const run = L ? L.begin(SLUG, specs.map((s) => hash32(designKey(s)))) : null;
    if (L && !run) { el['sm-batch'].close(); return; }
    const status = el['sm-batch-status'];
    busy(el['sm-batch-run'], true);
    stage('exporting', { kind: 'batch', n: rows.length });
    try {
        if (!window.__batchDeliver) throw new Error('batch primitive missing');
        // FIRST await in the click: the folder picker needs the user activation
        const batch = await window.__batchDeliver.openBatch({ zipName: fileBase('zip'), slug: SLUG, total: rows.length });
        const N = window.__outputName;
        const names = [];
        let faces = null;
        for (let i = 0; i < specs.length; i++) {
            const s = normalise(specs[i]);
            status.textContent = tf('batch_progress', { i: i + 1, n: specs.length }, ':i / :n');
            let blob;
            if (fmt === 'svg') {
                const sc = await prepare(s, { exporting: true });
                if (!faces) { try { faces = await outlinesFor(sc); } catch (e) { faces = null; } }
                blob = new Blob([sceneToSvg(sc, { outlines: faces, padMm: 0.5, title: mainOf(s) })], { type: 'image/svg+xml' });
            } else {
                const pxPerMm = 300 / 25.4;
                const sc = await prepare(s, { exporting: true });
                const inked = inkActive(s.ink);
                let c = renderScene(sc, { pxPerMm, padMm: inked ? inkPad(s.w) : 0.5, bg: null });
                if (inked) c = await compose(c, Object.assign({}, s.ink, { seed: (s.ink.seed + i * 2654435761) >>> 0 }), pxPerMm);
                blob = await canvasBlob(c, 'image/png');
            }
            let name = N ? N.sanitize(mainOf(s) || ('stamp-' + (i + 1))) : ('stamp-' + (i + 1));
            name = N ? N.distinct(N.join(name, fmt), names) : name + '.' + fmt;
            names.push(name);
            await batch.write(name, blob);
        }
        const res = await batch.finish();
        if (res && res.via !== 'refused' && res.via !== 'declined' && res.via !== 'empty') {
            run && run.commit();
            status.textContent = tf('batch_done', { n: res.written || specs.length }, 'Done — :n stamps');
            feature('batch', { n: specs.length, fmt, via: res.via });
            try { window.__aeToolComplete && window.__aeToolComplete({ f: 'batch', kind: fmt, n: specs.length, via: res.via }); } catch (e) {}
        } else { run && run.abort(); status.textContent = ''; stage('ready'); }
    } catch (e) { run && run.abort(); status.textContent = ''; exportFailed(e, 'batch'); }
    finally { busy(el['sm-batch-run'], false); }
}
function mainOf(s) { const p = PRESETS[s.preset]; for (const f of p.fields) { const v = (s.fields[f.id] || '').trim(); if (v && f.kind !== 'date') return v.split(/\r?\n/)[0]; } return ''; }

/* ── misc ──────────────────────────────────────────────────────────────── */
function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot().catch((e) => fail(e, 'boot')));
else boot().catch((e) => fail(e, 'boot'));
